import React from 'react';

import { AdaptiveSingleLineText, StatusTag } from '@components/common';
import CategoriesList from '@components/common/CategoriesList';
import TextAvatar from '@components/common/TextAvatar';

import { useQueryOrganisationProfileById } from '@hooks/organisation/useQueryOrganisationProfile';

import { formatDate } from '@utils/date';
import { categoriesFormatted } from '@utils/formatter';
import { formatPhoneNumber } from '@utils/phoneUtils';

import { EStatus } from '@enums/common';

import styles from './OrganisationInfo.module.scss';

interface IOrganisationInfoProps {
	id: string;
}

const OrganisationInfo = ({ id }: IOrganisationInfoProps) => {
	const { profile } = useQueryOrganisationProfileById(id);

	const categories = profile?.categories && categoriesFormatted(profile.categories);

	const isContactInfo = profile?.email || profile?.phoneNumber;

	const LINE_WIDTH = 400;

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<TextAvatar
					avatarId={profile?.avatar?.id}
					className={styles.logo}
					fontSize='20px'
					fullName={profile.name}
					id={profile.id}
					size='80px'
				/>
				<div className={styles.name}>{profile.name}</div>
				<div className={styles.organisationType}>{profile.types?.map(({ title }: {
					title: string;
				}) => title).join(', ')}</div>
				<div className={styles.status}>
					<StatusTag status={profile.isActive ? EStatus.active : EStatus.blocked} />
				</div>
			</div>
			<div className={styles.infoSection}>
				<div className={styles.title}>Basic information</div>
				<hr />
				<div className={styles.infoItem}>
					<div className={styles.label}>Legal name</div>
					<div className={styles.value}>{profile.legalName}</div>
				</div>
				{profile.websiteURL && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Website URL</div>
						<a
							className={styles.value}
							href={profile.websiteURL}
							rel='noreferrer'
							target='_blank'
						>
							<AdaptiveSingleLineText
								maxWidth={LINE_WIDTH}
								paddingsSize={0}
								styleInline={{ padding: '0' }}
								text={profile.websiteURL}
							/>
						</a>
					</div>
				)}
				{profile.companyRegistrationNumber && (
					<div className={styles.infoItem}>
						<div className={styles.label}>CRN</div>
						<div className={styles.value}>{profile.companyRegistrationNumber}</div>
					</div>
				)}
				{profile.createdAt && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Creation date:</div>
						<div className={styles.value}>{formatDate(profile.createdAt)}</div>
					</div>
				)}
				{profile.updatedAt && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Update date:</div>
						<div className={styles.value}>{formatDate(profile.updatedAt)}</div>
					</div>
				)}
				{categories && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Categories</div>
						<div className={styles.value}>
							<CategoriesList categories={categories} />
						</div>
					</div>
				)}
			</div>
			<div className={styles.infoSection}>
				<div className={styles.title}>Address</div>
				<hr />
				{profile.nameProperty && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Property No./Name</div>
						<div className={styles.value}>{profile.nameProperty}</div>
					</div>
				)}
				{profile.streetName && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Street Name</div>
						<div className={styles.value}>{profile.streetName}</div>
					</div>
				)}
				{profile.locality && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Locality</div>
						<div className={styles.value}>{profile.locality}</div>
					</div>
				)}
				{profile.city && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Town/City</div>
						<div className={styles.value}>{profile.city}</div>
					</div>
				)}
				{profile.postcode && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Postcode</div>
						<div className={styles.value}>{profile.postcode}</div>
					</div>
				)}
				{profile?.country?.name && (
					<div className={styles.infoItem}>
						<div className={styles.label}>Country</div>
						<div className={styles.value}>{profile.country.name}</div>
					</div>
				)}
			</div>
			{isContactInfo && (
				<div className={styles.infoSection}>
					<div className={styles.title}>Contacts</div>
					<hr />
					{profile.email && (
						<div className={styles.infoItem}>
							<div className={styles.label}>Contact Email</div>
							<AdaptiveSingleLineText
								maxWidth={LINE_WIDTH}
								paddingsSize={0}
								styleInline={{ padding: '0' }}
								text={profile.email}
							/>
						</div>
					)}
					{profile.phoneNumber && (
						<div className={styles.infoItem}>
							<div className={styles.label}>Contact Number</div>
							<div className={styles.value}>{formatPhoneNumber(profile.phoneNumber)}</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default OrganisationInfo;
